import { useState, useRef, useEffect } from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { FilterSiteItem } from '@/api/dataVault';

interface Props {
  sites: FilterSiteItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
}

export function SiteSelector({ sites, selected, onChange, loading }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = sites.filter(
    (s) => s.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectedNames = sites
    .filter((s) => selected.includes(s.id))
    .map((s) => s.name);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors min-w-[180px] max-w-[300px]"
        style={{
          background: 'var(--dv-bg-input)',
          border: '1px solid var(--dv-border-default)',
          color: 'var(--dv-text-primary)',
        }}
      >
        <span className="truncate flex-1 text-left">
          {selected.length === 0
            ? 'ALL SITES'
            : selected.length === 1
              ? selectedNames[0]
              : `${selected.length} SITES`}
        </span>
        <ChevronDownIcon className="h-3 w-3 text-mono-500 shrink-0" />
      </button>

      {open && (
        <div
          // Use the fully-opaque card variant — the translucent --dv-bg-card
          // works for cards on a solid page background, but a floating
          // dropdown over a chart canvas needs to fully cover what's behind
          // it (Stiff diagram text was bleeding through).
          className="absolute z-50 top-full mt-1 left-0 w-72 rounded shadow-xl max-h-80 flex flex-col"
          style={{
            background: 'var(--dv-bg-card-solid)',
            border: '1px solid var(--dv-border-default)',
          }}
        >
          <div className="p-2" style={{ borderBottom: '1px solid var(--dv-border-subtle)' }}>
            <input
              type="text"
              placeholder="Search sites..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-xs px-2 py-1 rounded focus:outline-none"
              style={{
                background: 'var(--dv-bg-input)',
                border: '1px solid var(--dv-border-default)',
                color: 'var(--dv-text-primary)',
              }}
              autoFocus
            />
          </div>
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setSearch(''); }}
              className="px-3 py-1.5 text-xs text-left tracking-wider transition-colors"
              style={{ color: 'var(--dv-text-muted)' }}
            >
              CLEAR ALL
            </button>
          )}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="px-3 py-4 text-xs tracking-wider" style={{ color: 'var(--dv-text-muted)' }}>LOADING...</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs tracking-wider" style={{ color: 'var(--dv-text-muted)' }}>NO SITES FOUND</div>
            ) : (
              filtered.map((site) => (
                <button
                  key={site.id}
                  onClick={() => toggle(site.id)}
                  className="w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2"
                  style={{
                    color: selected.includes(site.id) ? 'var(--dv-text-primary)' : 'var(--dv-text-secondary)',
                    background: selected.includes(site.id) ? 'var(--dv-bg-card-hover)' : 'transparent',
                  }}
                >
                  <span
                    className="w-3 h-3 rounded border shrink-0 flex items-center justify-center"
                    style={{
                      borderColor: selected.includes(site.id) ? 'var(--dv-accent-cyan)' : 'var(--dv-border-default)',
                      background: selected.includes(site.id) ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    }}
                  >
                    {selected.includes(site.id) && (
                      <span className="w-1.5 h-1.5 rounded-sm" style={{ background: 'var(--dv-accent-cyan)' }} />
                    )}
                  </span>
                  <span className="truncate">{site.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
